<script setup lang="ts">
import VideoCallRoomCard from "~/components/video-call/VideoCallRoomCard.vue";
import type {
  VideoCallParticipant,
  VideoCallRoomsSnapshot,
} from "~/types/videoCalls";

const props = defineProps<{
  snapshot: VideoCallRoomsSnapshot;
  disabled: boolean;
  movingParticipantId: string | null;
}>();

const emit = defineEmits<{
  rename: [roomId: string, name: string];
  remove: [roomId: string];
  enter: [roomId: string];
  move: [participantId: string, roomId: string];
}>();

const rooms = computed(() =>
  [...props.snapshot.rooms].sort((a, b) => a.order - b.order),
);
const prepared = computed(() => props.snapshot.state === "prepared");
const roomParticipants = (roomId: string): VideoCallParticipant[] =>
  (props.snapshot.roster ?? []).filter((participant) => {
    if (["left", "removed"].includes(participant.state)) return false;
    const assignedRoom =
      prepared.value && participant.role !== "host"
        ? props.snapshot.planned_assignments?.[participant.id]
        : participant.room_id;
    return (assignedRoom || props.snapshot.main_room_id) === roomId;
  });
const helpParticipantIds = (roomId: string): string[] =>
  props.snapshot.help_requests
    .filter((request) => request.room_id === roomId)
    .map((request) => request.participant_id);
</script>

<template>
  <div class="rooms-board" data-testid="video-call-rooms-board">
    <VideoCallRoomCard
      v-for="room in rooms"
      :key="room.id"
      :room="room"
      :rooms="rooms"
      :participants="roomParticipants(room.id)"
      :help-participant-ids="helpParticipantIds(room.id)"
      :current-room-id="props.snapshot.current_room_id"
      :prepared="prepared"
      :disabled="props.disabled"
      :moving-participant-id="props.movingParticipantId"
      @rename="emit('rename', room.id, $event)"
      @remove="emit('remove', room.id)"
      @enter="emit('enter', room.id)"
      @move="(participantId, roomId) => emit('move', participantId, roomId)"
    />
  </div>
</template>

<style scoped>
.rooms-board {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(230px, 1fr));
  align-items: stretch;
  gap: 14px;
}

@media (max-width: 600px) {
  .rooms-board {
    grid-template-columns: minmax(0, 1fr);
  }
}
</style>
